import parseMap from '../../scripts/parseMap.mjs'
import extendNode from '../extendNode.mjs'
import Color from '../Color.mjs'
import useProductNode from '../_PRODUCT/useProductNode.jsx'
import IconLetters from '../icons/IconLetters.jsx'
import meta from './metaUi.mjs'
import Model from './ModelUi.mjs'
import PropertiesPane from './PropertiesPaneUi.jsx'
import FormChoose from './FormChooseUi.jsx'
import TableChoose from './TableChooseUi.jsx'
import api from './apiUi.mjs'
import useContextMenu from './useContextMenuUi.mjs'
import usePreviewUi from './usePreviewUi.jsx'

export default () => {
    const ProductNode = useProductNode()
    const contextMenu = useContextMenu()
    const previewUi = usePreviewUi()

    return extendNode(ProductNode, {
        ...meta,
        api,
        contextMenu,
        FormChoose,
        TableChoose,
        Model,
        PropertiesPane,

        get readHierarchyMap() {
            const {readHierarchyMap} = this.api

            if (! readHierarchyMap) {
                return void 0
            }

            return async args => {
                const mapJson = await readHierarchyMap(args)
                return parseMap(mapJson)
            }
        },

        get updateHierarchy() {
            const {updateHierarchy} = this.api

            if (! updateHierarchy) {
                return void 0
            }

            return async (map, node) => {
                const tree = this.exportTree(map, node)
                await updateHierarchy(tree)
            }
        },

        async choose(map, node) {
            const getQuery = ({bms = [], fms = [], ss, ...query}) => ({
                ...query,
                bmIds: bms.map(({bmId}) => bmId),
                fmIds: fms.map(({fmId}) => fmId),
                ssId: ss?.ssId,
            })

            return this._choose(map, node, {getQuery})
        },

        clean(map, node) {
            ProductNode.clean.call(this, map, node)

            const {
                migrateMap,
                ...data
            } = node.data

            node.data = data
        },

        getIcons(map, node) {
            if (node.data.pkid) {
                return []
            }
            else {
                return [
                    <IconLetters
                        key="type"
                        fill={Color.BLUE}
                        letters="U"
                        textColor="#fff"
                    />
                ]
            }
        },

        async onDoubleClick(map, node, event) {
            if (event.ctrlKey) {
                await previewUi(map, node, '_self')
            }
            else {
                await ProductNode.onDoubleClick.call(this, map, node, event)
            }
        },

        _pushDataSlots: {
            cAlgList: [],
            inputAlgList: [],
            outputAlgList: [],
            ruleList: [],
            umUeList: [],
            uvList: [],
            varAlgList: [],
        },

        async _atAttach(map, node, event) {
            await ProductNode._atAttach.call(this, map, node, event)

            if (event.target === node) {
                await this._onInsert(map, node)
            }
        },

        async _atCreate(map, node) {
            for (const n of map.chain(node.parent)) {
                const {
                    bizNodeType,
                    bmCode,
                    bmId,
                    bmName,
                    fmCode,
                    fmId,
                    fmName,
                    ssCode,
                    ssId,
                    ssName,
                } = n.data

                if (/^CAT_ROOT_[BFU]M$/.test(bizNodeType)) {
                    node.data = {
                        ...node.data,
                        bmCode,
                        bmId,
                        bmName,
                        fmCode,
                        fmId,
                        fmName,
                        ssCode,
                        ssId,
                        ssName,
                    }

                    break
                }
            }

            await ProductNode._atCreate.call(this, map, node)
        },

        _getCategories(map, node) {
            return [
                'UI_DEMO',
                'UI_PARAM',
                'UI_RULE',
                'UI_CALG',
                'UI_UE',
            ]
        },

        _getDefaultDataFields(map, node) {
            return {
                ...ProductNode._getDefaultDataFields.call(this, map, node),
                independentUiFlag: '1',
            }
        },

        _getShape(map, node) {
            return 'Rectangle'
        },

        _getThemeColor(map, node) {
            return Color.LIGHT_BLUE
        },

        async _grow(map, node, depth) {
            const newDepth = await ProductNode._grow.call(
                this, map, node, depth
            )

            if (newDepth === depth) {
                return depth
            }

            const {isMigrate, mapTypeCode, subTypeCode} = map.data

            if (
                1 !== isMigrate &&
                ! /^(BDM|UI)_MAP$/.test(mapTypeCode)
            ) {
                if (
                    'BODA2' === subTypeCode &&
                    /^(BIZ|DATA|DEP|FC|SA|SF|UI)_MAP$/.test(mapTypeCode)
                ) {
                    for (const n of [...node.children]) {
                        const {bizNodeType} = n.data

                        if ('UI_UE' === bizNodeType) {
                            map.deleteNode(n)
                        }
                        else {
                            map.deleteTree(n)
                        }
                    }
                }
                else {
                    const p = node.trueParent

                    if (p) {
                        const {bizNodeType} = p.data

                        if ('FRES' === bizNodeType) {
                            const next = chain => {
                                const _n = map.nodeProxy(chain[0])
                                const isMounted = _n.isMounted()

                                return {
                                    yieldChildren: isMounted,
                                    yieldNode: isMounted,
                                }
                            }

                            for (const n of map.dfs(node.children, next)) {
                                const {bizNodeType, ueTypeCode} = n.data

                                if (
                                    'UE' === bizNodeType &&
                                    'INI' === ueTypeCode
                                ) {
                                    map.unlinkTree(n)
                                    map.prependChild(node, n)

                                    while (n.nextSibling) {
                                        map.deleteTree(n.nextSibling)
                                    }
                                }
                            }
                        }
                    }
                }
            }

            return newDepth
        },

        async _onChange(map, node, event) {
            if (node !== event.target) {
                return
            }

            const oldData = event.detail

            if (node.data.layoutTypeCode !== oldData.layoutTypeCode) {
                node.data = {
                    ...node.data,
                    layoutPicId: '',
                    utName: '',
                }
            }

            if (
                node.data.vmCode === oldData.vmCode &&
                node.data.ssCode !== oldData.ssCode
            ) {
                node.data = {
                    ...node.data,
                    vmCode: '',
                    vmId: '',
                    vmName: '',
                }
            }
        },

        async _onInsert(map, node) {
            await (async () => {
                const p = node.trueParent

                if (! p) {
                    return
                }

                const {bizNodeType} = p.data

                if ('AR' === bizNodeType) {
                    const {pkid} = node.data
                    const treeData = await this.readTree({pkid})
                    const ui = map.importTree(treeData)

                    const uiInput = (() => {
                        for (const n of ui.children) {
                            if ('UI_INPUT' === n.data.bizNodeType) {
                                return n
                            }
                        }
                    })()

                    if (! uiInput) {
                        map.deleteTree(ui)
                        return
                    }

                    const addAr = node.nextSibling ?
                        ar => map.insertSiblingBefore(node.nextSibling, ar) :
                        ar => map.appendChild(node.parent, ar)

                    const next = (chain) => {
                        const {bizNodeType: t} = chain[0].data
                        const yieldNode = 'AR' === t

                        const yieldChildren = (
                            'AR' === t ||
                            map.BizNode[t].classes.includes('category')
                        )

                        return {yieldChildren, yieldNode}
                    }

                    for (const n of map.dfs(uiInput.children, next)) {
                        const {
                            arTypeCode,
                            [map.BizNode.AR.textProp]: t,
                        } = n.data

                        if ('VAR' !== arTypeCode) {
                            continue
                        }

                        const text = t.replace(
                            /^(?:<IP>)? *(\$*.*)$/,
                            '<IP> $1'
                        )

                        const data = {
                            ...n.data,
                            arTypeCode: 'SET_IP',
                            [map.BizNode.AR.textProp]: text,
                        }

                        const ar = map.importTree({data})
                        addAr(ar)
                    }

                    map.deleteTree(ui)
                }
            })()

            await ProductNode._onInsert.call(this, map, node)
        },

        async _readGrowTree(map, node) {
            const {isMigrate, mapTypeCode} = map.data

            if (
                1 === isMigrate ||
                /^(BDM|UI)_MAP$/.test(mapTypeCode)
            ) {
                const {pkid} = node.data
                const {migrateMap} = await api.readMigrateMap({pkid})
                const {root} = parseMap(migrateMap)
                return root
            }
            else {
                return ProductNode._readGrowTree.call(this, map, node)
            }
        },
    })
}
